import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Trash2 } from "lucide-react";
import {
  api,
  setInstanceName,
  setRecycleDefaultRecoverableOnly,
  type ConfigResponse,
} from "@/lib/api";
import { MetadataSyncSection } from "@/components/MetadataSyncSection";

export function SettingsPage() {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ["config"],
    queryFn: async () => (await api.get<ConfigResponse>("/config")).data,
  });

  return (
    <div className="mx-auto max-w-3xl p-6 space-y-4">
      <h1 className="text-xl font-semibold">Settings</h1>

      <InstanceNameSection
        currentValue={data?.instanceName ?? null}
        onSaved={() => {
          qc.invalidateQueries({ queryKey: ["config"] });
          qc.invalidateQueries({ queryKey: ["app-config"] });
        }}
      />

      <CognosInstanceSection
        dispatcherUrl={data?.cognosDispatcherUrl ?? null}
        namespace={data?.cognosNamespace ?? null}
        username={data?.cognosUsername ?? null}
      />

      <RecycleDefaultsSection
        currentValue={data?.recycleDefaultRecoverableOnly ?? true}
        onSaved={() => qc.invalidateQueries({ queryKey: ["config"] })}
      />

      <section data-tour-id="settings-llm" className="rounded border border-datumcraft-slate/20 bg-white p-4">
        <h2 className="text-sm font-semibold">LLM provider</h2>
        <p className="mt-1 text-sm">{data?.llmProvider ?? "—"}</p>
      </section>
      <section data-tour-id="settings-semantic" className="rounded border border-datumcraft-slate/20 bg-white p-4">
        <h2 className="text-sm font-semibold">Semantic layer</h2>
        <p className="mt-1 text-sm font-mono text-xs">{data?.semanticLayerVersion ?? "—"}</p>
      </section>
      <section className="rounded border border-datumcraft-slate/20 bg-white p-4">
        <h2 className="text-sm font-semibold">Feature flags</h2>
        <dl className="mt-2 grid grid-cols-2 gap-2 text-sm">
          {Object.entries(data?.featureFlags ?? {}).map(([k, v]) => (
            <div key={k} className="flex justify-between">
              <dt>{k}</dt>
              <dd>{v ? "on" : "off"}</dd>
            </div>
          ))}
        </dl>
      </section>
      {/* Hidden automatically when datumcraft.metadata.lens.enabled=false on the
          backend — the component detects a 404 on /metadata-sync/status and
          returns null. */}
      <MetadataSyncSection />
    </div>
  );
}

/**
 * Editable deployment label. Operators running multiple bundles
 * (dev / staging / customer A / customer B) set a name here; it
 * appears as a chip in every product page header. Admin-only — the
 * PUT endpoint is gated by {@code @PreAuthorize hasAnyRole(ADMIN,
 * SUPER_ADMIN)}; non-admins get a 403 surfaced in the form's error
 * banner.
 */
function InstanceNameSection({
  currentValue,
  onSaved,
}: {
  currentValue: string | null;
  onSaved: () => void;
}) {
  // Local input state, hydrated from the server when the query lands.
  // Tracking dirty state means we only POST on real change, and the
  // Save button can disable when the value matches the persisted one.
  const [draft, setDraft] = useState(currentValue ?? "");
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setDraft(currentValue ?? "");
  }, [currentValue]);

  const save = useMutation({
    mutationFn: () => setInstanceName(draft.trim() || null),
    onSuccess: () => {
      setError(null);
      setSaved(true);
      onSaved();
      // Hide the "Saved" affordance after a short window — same UX
      // shape every other inline-save form in this app uses.
      setTimeout(() => setSaved(false), 2500);
    },
    onError: (e: unknown) => {
      const msg = e instanceof Error ? e.message : String(e);
      setError(msg.includes("403") ? "Admin role required to set instance name." : msg);
    },
  });

  const dirty = (draft.trim() || null) !== (currentValue ?? null);
  const tooLong = draft.length > 200;

  return (
    <section className="rounded border border-datumcraft-slate/20 bg-white p-4">
      <h2 className="text-sm font-semibold">Instance name</h2>
      <p className="mt-1 text-2xs text-datumcraft-slate">
        Shown as a chip in the header so you can distinguish multiple
        deployments at a glance — dev / staging / customer A / customer B.
        Admin-only.
      </p>
      <form
        className="mt-2 flex items-center gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (!dirty || tooLong) return;
          save.mutate();
        }}
      >
        <input
          type="text"
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            setError(null);
            setSaved(false);
          }}
          maxLength={200}
          placeholder="e.g. Production · Customer ACME"
          className="flex-1 rounded-md border border-datumcraft-slate/30 bg-white px-2 py-1.5 text-sm focus:border-datumcraft-accentDark focus:outline-none focus:ring-2 focus:ring-datumcraft-accentDark/20"
        />
        <button
          type="submit"
          disabled={!dirty || tooLong || save.isPending}
          className="inline-flex items-center gap-1 rounded-md bg-datumcraft-navy px-3 py-1.5 text-xs font-medium text-white shadow-sm transition hover:bg-datumcraft-navy/90 disabled:cursor-not-allowed disabled:opacity-40"
        >
          {save.isPending && <Loader2 size={11} className="animate-spin" />}
          Save
        </button>
        {currentValue && draft.trim() && (
          <button
            type="button"
            onClick={() => {
              setDraft("");
              setError(null);
              save.mutate();
            }}
            className="inline-flex items-center rounded-md border border-datumcraft-slate/30 bg-white px-3 py-1.5 text-xs font-medium text-datumcraft-ink hover:bg-datumcraft-mist"
          >
            Clear
          </button>
        )}
      </form>
      {tooLong && (
        <p className="mt-1.5 text-2xs text-rose-600">
          200 characters max. Currently {draft.length}.
        </p>
      )}
      {error && <p className="mt-1.5 text-2xs text-rose-600">{error}</p>}
      {saved && !error && <p className="mt-1.5 text-2xs text-emerald-600">Saved.</p>}
    </section>
  );
}

/**
 * Recycle-bin default for the cognos.recycle.list tool's
 * recoverableOnly param. Settings-page toggle so admins can flip
 * the global default without a bundle restart. Resolution order
 * (per docs/designs/cognos-recycle-bin-tools.md §6.2): per-call
 * value > DB setting (this control) > env var > true.
 */
function RecycleDefaultsSection({
  currentValue,
  onSaved,
}: {
  currentValue: boolean;
  onSaved: () => void;
}) {
  const [draft, setDraft] = useState(currentValue);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setDraft(currentValue);
  }, [currentValue]);

  const save = useMutation({
    mutationFn: () => setRecycleDefaultRecoverableOnly(draft),
    onSuccess: () => {
      setError(null);
      setSaved(true);
      onSaved();
      setTimeout(() => setSaved(false), 2500);
    },
    onError: (e: unknown) => {
      const msg = e instanceof Error ? e.message : String(e);
      setError(msg.includes("403") ? "Admin role required to change this default." : msg);
    },
  });

  const dirty = draft !== currentValue;

  return (
    <section className="rounded border border-datumcraft-slate/20 bg-white p-4">
      <h2 className="inline-flex items-center gap-2 text-sm font-semibold">
        <Trash2 size={13} aria-hidden /> Recycle-bin list defaults
      </h2>
      <p className="mt-1 text-2xs text-datumcraft-slate">
        What <code className="font-mono">cognos.recycle.list</code> shows when no{" "}
        <code className="font-mono">recoverableOnly</code> override is passed.
        Per-call values always win; this is just the default. Admin-only.
      </p>

      <fieldset className="mt-3 space-y-1.5 text-sm">
        <label className="flex cursor-pointer items-start gap-2">
          <input
            type="radio"
            name="recycle-default"
            checked={draft === true}
            onChange={() => {
              setDraft(true);
              setError(null);
              setSaved(false);
            }}
            className="mt-1 accent-datumcraft-navy"
          />
          <span>
            <span className="font-medium text-datumcraft-ink">Recoverable only</span>{" "}
            (state_id = 2)
            <br />
            <span className="text-2xs text-datumcraft-slate">
              Hides past-retention items. The "what can I actually undo right now"
              view.
            </span>
          </span>
        </label>
        <label className="flex cursor-pointer items-start gap-2">
          <input
            type="radio"
            name="recycle-default"
            checked={draft === false}
            onChange={() => {
              setDraft(false);
              setError(null);
              setSaved(false);
            }}
            className="mt-1 accent-datumcraft-navy"
          />
          <span>
            <span className="font-medium text-datumcraft-ink">All deleted items</span>{" "}
            (including past-retention)
            <br />
            <span className="text-2xs text-datumcraft-slate">
              Shows everything in state_id 2 + 3. Past-retention rows can't be
              restored — they're informational.
            </span>
          </span>
        </label>
      </fieldset>

      <div className="mt-3 flex items-center gap-2">
        <button
          type="button"
          disabled={!dirty || save.isPending}
          onClick={() => save.mutate()}
          className="inline-flex items-center gap-1 rounded-md bg-datumcraft-navy px-3 py-1.5 text-xs font-medium text-white shadow-sm transition hover:bg-datumcraft-navy/90 disabled:cursor-not-allowed disabled:opacity-40"
        >
          {save.isPending && <Loader2 size={11} className="animate-spin" />}
          Save
        </button>
        {error && <span className="text-2xs text-rose-600">{error}</span>}
        {saved && !error && <span className="text-2xs text-emerald-600">Saved.</span>}
      </div>
    </section>
  );
}

/**
 * Read-only Cognos connection summary. Sourced from the bundle's
 * {@code datumcraft.cognos.sdk.*} env block — same values the SDK
 * authenticates with. Password is intentionally NOT exposed by the
 * backend, so the section can be safely visible to every authenticated
 * user.
 */
function CognosInstanceSection({
  dispatcherUrl,
  namespace,
  username,
}: {
  dispatcherUrl: string | null;
  namespace: string | null;
  username: string | null;
}) {
  const allEmpty = !dispatcherUrl && !namespace && !username;

  return (
    <section className="rounded border border-datumcraft-slate/20 bg-white p-4">
      <h2 className="text-sm font-semibold">Cognos instance</h2>
      <p className="mt-1 text-2xs text-datumcraft-slate">
        Where Operate is connecting to read content + audit data.
        Sourced from the bundle's environment ({" "}
        <code className="font-mono">datumcraft.cognos.sdk.*</code>); change in{" "}
        <code className="font-mono">conf/.env</code> and restart to update.
      </p>
      {allEmpty ? (
        <p className="mt-2 rounded-md bg-datumcraft-mist px-3 py-2 text-2xs text-datumcraft-slate">
          Cognos SDK is disabled on this deployment — content reads use the
          audit DB only. Set <code className="font-mono">CLOUDCRAFT_COGNOS_SDK_ENABLED=true</code>{" "}
          and the matching dispatcher / credentials in{" "}
          <code className="font-mono">conf/.env</code> to enable live SDK calls.
        </p>
      ) : (
        <dl className="mt-2 grid grid-cols-1 gap-2 text-sm">
          <Row label="Dispatcher URL" value={dispatcherUrl} mono />
          <Row label="Namespace" value={namespace} />
          <Row label="Service account" value={username} />
        </dl>
      )}
    </section>
  );
}

function Row({
  label,
  value,
  mono,
}: {
  label: string;
  value: string | null | undefined;
  mono?: boolean;
}) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="shrink-0 text-2xs font-medium uppercase tracking-wider text-datumcraft-slate">
        {label}
      </dt>
      <dd
        className={`min-w-0 truncate text-right text-datumcraft-ink ${
          mono ? "font-mono text-xs" : "text-sm"
        }`}
        title={value ?? undefined}
      >
        {value && value.length > 0 ? value : "—"}
      </dd>
    </div>
  );
}
